// /Users/charispetsas/Code/GEOIMAGING/MSP4Growth/frontend/msp/src/components/map/LayerControl.js
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import ReactDOM from 'react-dom/client';
import { Box, FormControlLabel, Checkbox, Typography, RadioGroup, Radio } from '@mui/material';


const LayerControlUI = ({
    layers = [],
    layerVisibility,
    onVisibilityChange,
    baseLayer = 'osm',
    onBaseLayerChange,
    baseOptions = {}
}) => {
    return (
        <Box sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '10px',
            borderRadius: '5px',
            boxShadow: '0 1px 5px rgba(0,0,0,0.65)',
            minWidth: '180px',
            maxHeight: '70vh',
            overflowY: 'auto'
        }}>
            {Object.keys(baseOptions).length > 0 && (
                <>
                    <Typography variant="subtitle2" sx={{ marginBottom: '5px', fontWeight: 'bold' }}>Base map</Typography>
                    <RadioGroup
                        value={baseLayer}
                        onChange={(e) => onBaseLayerChange && onBaseLayerChange(e.target.value)}
                    >
                        {Object.values(baseOptions).map(option => (
                            <FormControlLabel
                                key={option.id}
                                value={option.id}
                                control={<Radio size="small" />}
                                label={option.label || option.id}
                            />
                        ))}
                    </RadioGroup>
                </>
            )}
            <Typography variant="subtitle2" sx={{ marginBottom: '5px', fontWeight: 'bold', marginTop: '10px' }}>Layers</Typography>
            {layers.map(layer => (
                <FormControlLabel
                    key={layer.id}
                    control={
                        <Checkbox
                            checked={!!layerVisibility[layer.id]}
                            onChange={(e) => onVisibilityChange(layer.id, e.target.checked)}
                            size="small"
                        />
                    }
                    label={layer.name}
                    sx={{ display: 'block', textAlign: 'left' }}
                />
            ))}
        </Box>
    );
};

const LayerControl = ({
    layers = [],
    layerVisibility,
    onVisibilityChange,
    baseLayer = 'osm',
    onBaseLayerChange,
    baseOptions = {}
}) => {
    const map = useMap();
    const controlRef = useRef(null);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!map || controlRef.current) return;

        const control = L.control({ position: 'topright' }); 

        control.onAdd = () => {
            const div = L.DomUtil.create('div', 'leaflet-layer-control');
            L.DomEvent.disableClickPropagation(div);
            L.DomEvent.disableScrollPropagation(div);
            const root = ReactDOM.createRoot(div);
            rootRef.current = root;
            div.reactRoot = root; // fallback handle for cleanup
            return div;
        };

        control.onRemove = (mapInstance) => {
            const div = control.getContainer();
            if (div && div.reactRoot) {
                div.reactRoot.unmount();
                delete div.reactRoot;
            }
            rootRef.current = null;
        };

        control.addTo(map);
        controlRef.current = control;

        return () => {
            if (map && controlRef.current) {
                map.removeControl(controlRef.current);
                controlRef.current = null;
            }
            if (rootRef.current) {
                rootRef.current.unmount();
                rootRef.current = null;
            }
        };
    }, [map]);

    useEffect(() => {
        if (!rootRef.current) return;
        rootRef.current.render(
            <LayerControlUI
                layers={layers}
                layerVisibility={layerVisibility}
                onVisibilityChange={onVisibilityChange}
                baseLayer={baseLayer}
                onBaseLayerChange={onBaseLayerChange}
                baseOptions={baseOptions}
            />
        );
    }, [layers, layerVisibility, onVisibilityChange, baseLayer, onBaseLayerChange, baseOptions]);

    return null;
};

export default LayerControl;
